define([
    'lodash',
    'santa-components',
    'coreUtils',
    'expandableMenu/utils/expandableMenuUtils'
], function (
    _,
    santaComponents,
    coreUtils,
    expandableMenuUtils
) {
    'use strict';

    function isSelectedItem(dataItem) {
        return dataItem.isSelected;
    }

    function triggerClickOnAnchor(originOnClickCB, event) {
        const anchor = event.target.tagName === 'A' ? event.target : event.target.querySelector('a');

        if (anchor) {
            originOnClickCB(event);
            anchor.click();
            return false;
        }

        return true;
    }

    function getItemProperties(className, classPrefix, isSelected, heights, id, expandedMenus, callbacks, hasVisibleItems, dataItem, isResponsive) {
        const classSet = {};
        const isItemExpanded = !!expandedMenus[id];
        classSet[`${classPrefix}_selected`] = isSelected;
        classSet[`${classPrefix}_expanded`] = isItemExpanded;
        classSet[className] = true;

        const itemProperties = {
            className: coreUtils.classNames(classSet),
            style: isResponsive ? {} : {
                minHeight: Math.min(heights.line, heights.item),
                height: heights.item
            },
            'data-page-id': _.get(dataItem, ['link', 'pageId', 'id']),
            'aria-expanded': isItemExpanded,
            onClick: hasVisibleItems ? callbacks.click : undefined,
            id,
            key: id
        };

        if (isSelected) {
            itemProperties['aria-current'] = 'page';
        }

        return itemProperties;
    }

    function getItemsContainerProperties(hasVisibleItems, className, classPrefix/*, separatorHeight*/) {
        return {
            style: {},
            className: hasVisibleItems ? className : `${className} ${classPrefix}_emptySubMenu`
        };
    }

    function getLabelProperties(className, classPrefix, heights, link, level, callbacks, hasVisibleItems, isResponsive) {
        const properties = {
            className: `${className} ${classPrefix}_label level${level}`,
            style: isResponsive ? {} : {
                lineHeight: `${Math.min(heights.line, heights.item)}px`
            },
            'aria-haspopup': hasVisibleItems,
            onClick: hasVisibleItems ? callbacks.click : undefined
        };

        _.merge(properties, link ? link.render : {tabIndex: 0});

        return properties;
    }

    function getCounterProperties(classPrefix, heights, link, level, callbacks, hasVisibleItems, counterClassName, isResponsive) {
        const labelProps = getLabelProperties('', classPrefix, heights, link, level, callbacks, hasVisibleItems, isResponsive);
        labelProps.className = `${classPrefix}_${counterClassName} counter`;
        return labelProps;
    }

    function pushCounterWithProperties(items, classPrefix, heights, link, level, callbacks, hasVisibleItems, counterClassName, displayCount, isResponsive) {
        if (_.isNumber(displayCount)) {
            const counterProps = getCounterProperties(classPrefix, heights, link, level, callbacks, hasVisibleItems, counterClassName, isResponsive);
            items.push(santaComponents.utils.createReactElement('span', counterProps, `(${displayCount})`));
        }
    }

    const domBuilder = {
        getSkin(skinsMap, skinName) {
            return skinsMap.get(skinName);
        },
        buildTemplate(skinItem, classPrefix) {
            const tag = skinItem[0].toLowerCase();
            const rawItems = skinItem.slice(3);
            const items = [];
            const htmlAttributes = {};

            _.forEach(rawItems, child => {
                if (_.isArray(child)) {
                    items.push(domBuilder.buildTemplate(child, classPrefix));
                } else {
                    _.assign(htmlAttributes, child);
                }
            });

            return {
                tag,
                skinPart: skinItem[1],
                className: _.map(skinItem[2], currentClass => classPrefix + currentClass),
                htmlAttributes,
                items
            };
        },
        buildDomItem(mainTemplate, template, dataItems, dataItem, classPrefix, currentUrlPageId, isMobileView, heights, level, parentId, expandedMenus, callbacks, counterClassName, isResponsive, itemsAlignment) { // eslint-disable-line
            const tag = santaComponents.utils.createReactElement.bind(null, template.tag);
            const items = [];

            const id = `${parentId + dataItems.indexOf(dataItem)}_`;
            const hasVisibleItems = _.filter(dataItem.items, item => expandableMenuUtils.isVisible(item, isMobileView)).length > 0;

            const className = template.className
                .concat(dataItem.link ? '' : `${classPrefix}_noLink`)
                .join(' ')
                .trim();

            let properties = {
                className
            };

            switch (template.tag) {
                case 'a':
                    if (itemsAlignment === 'right') {
                        pushCounterWithProperties(items, classPrefix, heights, dataItem.link, level, callbacks, hasVisibleItems, counterClassName, dataItem.displayCount, isResponsive);
                        items.push(dataItem.label);
                        properties = getLabelProperties(properties.className, classPrefix, heights, dataItem.link, level, callbacks, hasVisibleItems, isResponsive);
                    } else {
                        items.push(dataItem.label);
                        properties = getLabelProperties(properties.className, classPrefix, heights, dataItem.link, level, callbacks, hasVisibleItems, isResponsive);
                        pushCounterWithProperties(items, classPrefix, heights, dataItem.link, level, callbacks, hasVisibleItems, counterClassName, dataItem.displayCount, isResponsive);
                    }
                    break;
                case 'ul':
                    _.forEach(dataItem.items, subItem => {
                        if (expandableMenuUtils.isVisible(subItem, isMobileView)) {
                            items.push(domBuilder.buildDomItem(mainTemplate, mainTemplate, dataItem.items, subItem, classPrefix, currentUrlPageId, isMobileView, heights, level + 1, id, expandedMenus, callbacks, counterClassName, isResponsive, itemsAlignment));
                        }
                    });
                    properties = getItemsContainerProperties(hasVisibleItems, properties.className, classPrefix, heights.separator);
                    break;
                case 'li':
                    properties.className += hasVisibleItems ? ` ${classPrefix}_hasSubList` : '';

                    _.forEach(template.items, templateItem => {
                        items.push(domBuilder.buildDomItem(mainTemplate, templateItem, dataItems, dataItem, classPrefix, currentUrlPageId, isMobileView, heights, level, parentId, expandedMenus, callbacks, counterClassName, isResponsive, itemsAlignment));
                    });
                    properties = getItemProperties(properties.className, classPrefix, isSelectedItem(dataItem), heights, id, expandedMenus, callbacks, hasVisibleItems, dataItem, isResponsive);
                    break;
                default:
                    _.forEach(template.items, templateItem => {
                        items.push(domBuilder.buildDomItem(mainTemplate, templateItem, dataItems, dataItem, classPrefix, currentUrlPageId, isMobileView, heights, level, parentId, expandedMenus, callbacks, counterClassName, isResponsive, itemsAlignment));
                    });
                    break;
            }

            const shouldHandleClick = _.includes(properties.className, 'Wrapper');
            if (shouldHandleClick) {
                properties.onClick = triggerClickOnAnchor.bind(null, callbacks.click);
            }

            _.defaults(properties, template.htmlAttributes);

            if (template.tag === 'ul') {
                return tag(properties, items);
            }

            return tag.apply(null, [properties].concat(items));
        },
        buildDOMFromTemplate(
            template,
            dataItems,
            classPrefix,
            currentUrlPageId,
            isMobileView,
            heights,
            expandedMenus,
            callbacks,
            counterClassName,
            isResponsive,
            itemsAlignment
        ) {
            const mainTemplate = template.items[0];
            const domItems = _(dataItems)
                .filter(item => expandableMenuUtils.isVisible(item, isMobileView))
                .map(item =>
                    domBuilder.buildDomItem(
                        mainTemplate,
                        mainTemplate,
                        dataItems,
                        item,
                        classPrefix,
                        currentUrlPageId,
                        isMobileView,
                        heights,
                        0,
                        'root',
                        expandedMenus,
                        callbacks,
                        counterClassName,
                        isResponsive,
                        itemsAlignment
                    )
                ).value();

            return santaComponents.utils.createReactElement('ul', {
                key: _.uniqueId(classPrefix),
                className: `${classPrefix}menuContainer`
            }, domItems);
        }
    };

    return domBuilder;
});
